if (!customElements.get("klaviyo-subscription-form")) {
  class KlaviyoSubscriptionForm extends HTMLElement {
    constructor() {
      super();

      this.onFormSubmitListener = this.onFormSubmit.bind(this);
    }

    get selectors() {
      return {
        fields: {
          submit: "[data-field-type='submit']",
          email: "[data-field-type='email']",
          phone: "[data-field-type='phone']"
        },
        message: "[data-message]"
      };
    }

    get attributes() {
      return {
        publicApiKey: "public-api-key",
        listId: "list-id",
        customSource: "custom-source",
        successMessage: "success-message"
      };
    }

    get modifiers() {
      return {
        hidden: "hidden",
        message: {
          success: "success",
          error: "error"
        }
      };
    }

    get messageTypes() {
      return {
        success: "success",
        error: "error"
      }
    }

    get publicApiKey() {
      return this.getAttribute(this.attributes.publicApiKey);
    }

    get listId() {
      return this.getAttribute(this.attributes.listId);
    }

    get customSource() {
      return this.getAttribute(this.attributes.customSource);
    }

    get successMessage() {
      return this.getAttribute(this.attributes.successMessage);
    }

    connectedCallback() {
      const submitElement = this.querySelector(this.selectors.fields.submit);

      if (!submitElement) {
        return;
      }

      submitElement.addEventListener("click", this.onFormSubmitListener);

      this.querySelectorAll("input").forEach((input) => {
        input.addEventListener("keydown", (event) => {
          if (event.key === "Enter") this.onFormSubmit(event);
        });
      });
    }

    async onFormSubmit(event) {
      event.preventDefault();

      if (this.isSubmitting || this.classList.contains("is-subscribed")) {
        return;
      }

      const emailInput = this.querySelector(this.selectors.fields.email);
      if (emailInput && !emailInput.checkValidity()) {
        this.onSubscriptionError(emailInput.value ? "Please enter a valid email address." : "Please enter your email address.");
        emailInput.focus();
        return;
      }

      const {
        email,
        phoneNumber
      } = this.extractProfileData();

      const profileAttributes = {
        email: email
      };

      if (phoneNumber) {
        profileAttributes.phone_number = phoneNumber;
      }

      this.setSubmitting(true);
      this.renderMessage();

      let response;
      try {
        response = await this.createClientSubscription(profileAttributes, this.customSource, this.listId);
      } catch (error) {
        console.error(error);
        this.setSubmitting(false);
        this.onSubscriptionError();
        return;
      }

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        console.error(data);

        this.setSubmitting(false);
        this.onSubscriptionError(data?.errors?.[0]?.detail);
        return;
      }

      this.setSubmitting(false);
      this.onSubscriptionSuccess(this.successMessage);
    }

    setSubmitting(isSubmitting) {
      this.isSubmitting = isSubmitting;
      this.classList.toggle("is-submitting", isSubmitting);

      const submitElement = this.querySelector(this.selectors.fields.submit);
      if (submitElement) {
        submitElement.disabled = isSubmitting;
        submitElement.setAttribute("aria-busy", String(isSubmitting));
      }
    }

    async createClientSubscription(profileAttributes, customSource, listId) {
      const url = `https://a.klaviyo.com/client/subscriptions/?company_id=${this.publicApiKey}`;

      if (!profileAttributes.subscriptions) {
        profileAttributes.subscriptions = {};
      }

      if (profileAttributes.email) {
        profileAttributes.subscriptions.email = {
          marketing: {
            consent: "SUBSCRIBED"
          }
        };
      }

      if (profileAttributes.phone_number) {
        profileAttributes.subscriptions.sms = {
          marketing: {
            consent: "SUBSCRIBED"
          }
        };
      }

      const options = {
        method: "POST",
        headers: {
          revision: "2025-01-15",
          "content-type": "application/vnd.api+json"
        },
        body: JSON.stringify({
          data: {
            type: "subscription",
            attributes: {
              ...(customSource && {
                custom_source: customSource
              }),
              profile: {
                data: {
                  type: "profile",
                  attributes: {
                    ...profileAttributes
                  }
                }
              }
            },
            relationships: {
              list: {
                data: {
                  type: "list",
                  id: listId
                }
              }
            }
          }
        })
      };

      return await fetch(url, options);
    }

    extractProfileData() {
      const email = this.querySelector(this.selectors.fields.email)?.value ?? "";
      const phoneNumber = this.querySelector(this.selectors.fields.phone)?.value ?? "";

      return {
        email,
        phoneNumber
      };
    }

    onSubscriptionError(message) {
      message = message ?? "Something went wrong. Please enter valid information and try again";
      this.renderMessage(this.messageTypes.error, message);
    }

    onSubscriptionSuccess(message) {
      message = message ?? "Thanks for subscribing! Check your email for a confirmation message.";
      const container = this.closest(".footer-newsletter__content") ?? this;
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const startHeight = container.offsetHeight;

      const reveal = () => {
        this.renderMessage(this.messageTypes.success, message);
        this.classList.add("is-subscribed");

        const messageElement = this.querySelector(this.selectors.message);
        if (messageElement) {
          messageElement.setAttribute("role", "status");
          messageElement.setAttribute("tabindex", "-1");
          messageElement.focus({ preventScroll: true });
        }

        if (reduceMotion) return;

        const endHeight = container.offsetHeight;
        container.animate(
          [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
          { duration: 450, easing: "cubic-bezier(0.22, 1, 0.36, 1)" }
        );
      };

      if (reduceMotion) {
        reveal();
        return;
      }

      container.classList.add("is-leaving");
      setTimeout(() => {
        container.classList.remove("is-leaving");
        reveal();
      }, 260);
    }

    renderMessage(type = null, message = "") {
      const messageElement = this.querySelector(this.selectors.message);

      if (!messageElement) {
        return;
      }

      messageElement.innerHTML = message;

      switch (type) {
        case this.messageTypes.success: {
          messageElement.classList.remove(this.modifiers.message.error);
          messageElement.classList.add(this.modifiers.message.success);

          messageElement.classList.remove(this.modifiers.hidden);

          break;
        }

        case this.messageTypes.error: {
          messageElement.classList.remove(this.modifiers.message.success);
          messageElement.classList.add(this.modifiers.message.error);

          messageElement.classList.remove(this.modifiers.hidden);

          break;
        }

        default: {
          messageElement.classList.add(this.modifiers.hidden);
        }
      }
    }
  }

  customElements.define("klaviyo-subscription-form", KlaviyoSubscriptionForm);
}

if (!customElements.get("klaviyo-custom-trigger")) {
  class KlaviyoCustomTrigger extends HTMLElement {
    constructor() {
      super();
    }

    get formId() {
      return this.getAttribute("form-id");
    }

    connectedCallback() {
      this.addEventListener("click", this.onClickListener.bind(this));
      this.addEventListener("keydown", this.onKeydownListener.bind(this));
    }

    openKlaviyoForm() {
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push([
        "openForm",
        this.formId.toString()
      ]);
    }

    onClickListener() {
      this.openKlaviyoForm();
    }

    onKeydownListener(event) {
      if (event.key === "Enter") {
        this.openKlaviyoForm();
      }
    }
  }

  customElements.define("klaviyo-custom-trigger", KlaviyoCustomTrigger);
}
